import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SvgIcon } from '@progress/kendo-react-common';
import {
    TextAreaHandle,
    TextArea as KendoTextArea,
    TextAreaProps as KendoTextAreaProps,
    InputSuffix,
    TextAreaChangeEvent,
} from '@progress/kendo-react-inputs';
import { Label } from '@progress/kendo-react-labels';
import { xIcon } from '@progress/kendo-svg-icons';
import cn from 'clsx';
import { useClipboard } from 'use-clipboard-copy';
import styles from './styles.module.scss';
import { CharactersValidationHint } from '@components-clean/core/characters-validation-hint';
import svgStyles from '@components-clean/core/drag-drop-lists-core/style.module.scss';
import InactiveIcon from '@components-clean/core/icons/inactive-icon/inactive-icon';
import InformationIcon from '@components-clean/core/icons/information-icon';
import ValidationIconSuffix from 'views-clean/components/core/icons/validation-icon-suffix';
import { useTranslation } from '@hooks/index';
import NotificationService from '@utils/notification';
import { validateLimitExceeded } from '@utils/validation';

interface TextAreaProps extends KendoTextAreaProps {
    'data-testid': string;
    validationMessage?: string;
    label?: string;
    inline?: boolean;
    showClear?: boolean;
    hintText?: string;
    hintTextTooltipPosition?: 'right' | 'left' | 'bottom' | 'top' | 'auto';
    hintTextMaxWidth?: number;
    copyBtn?: boolean;
    inactiveValue?: boolean;
    resizable?: 'none' | 'vertical' | 'horizontal' | 'both' | undefined;
    validationTooltipPosition?: 'right' | 'auto' | 'left';
}

const TextArea = ({
    children,
    placeholder,
    valid,
    validationMessage,
    label,
    inline,
    value,
    onChange,
    showClear,
    maxLength,
    'data-testid': testId,
    readOnly,
    hintText,
    hintTextTooltipPosition,
    hintTextMaxWidth,
    copyBtn,
    fillMode,
    disabled,
    inactiveValue,
    className,
    resizable,
    validationTooltipPosition,
    ...props
}: TextAreaProps) => {
    const { t } = useTranslation();
    const clipboard = useClipboard();

    const [inputValue, setInputValue] = useState<string | number | string[] | undefined>(value);

    const ref = useRef<TextAreaHandle>(null);

    const copyToClipboard = () => {
        clipboard.copy(value);
        NotificationService.showNotification(t('copiedToClipboard'));
    };

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setInputValue(value);
    }, [value]);

    const placeholderText = useMemo(() => {
        if (placeholder !== undefined) return placeholder;

        if (readOnly) return '';

        return t('cm2EnterValue');
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [placeholder, readOnly]);

    const suffixIconGroupClasses = cn('k-display-flex', 'k-align-items-center', {
        [styles.suffixGroupReadOnly]: readOnly,
    });

    const classes = cn(className, {
        'opra-inactive-state': inactiveValue,
    });

    return (
        <div
            className={cn(styles.textArea, {
                'k-flex-grow': !props.style?.width,
                'k-d-flex k-gap-4 k-align-items-center': inline,
            })}
        >
            <Label
                editorRef={ref}
                className={cn('k-d-flex k-align-items-center', {
                    '!k-pb-0 k-white-space-nowrap': inline,
                })}
            >
                {label}
                {hintText && (
                    <InformationIcon
                        title={hintText}
                        className="k-ml-1 k-font-size-md k-mt--0.5"
                        dataTestId={`${testId}-hint`}
                        tooltipPosition={hintTextTooltipPosition}
                        maxWidth={hintTextMaxWidth}
                    />
                )}
            </Label>
            <KendoTextArea
                {...props}
                className={classes}
                ref={ref}
                autoSize={!resizable}
                resizable={resizable}
                placeholder={!disabled ? placeholderText : ''}
                valid={valid}
                readOnly={readOnly}
                value={inputValue}
                fillMode={fillMode}
                disabled={disabled}
                onChange={(event) => {
                    setInputValue(event.target.value);
                    if (onChange) onChange(event);
                }}
                data-testid={testId}
                suffix={() => (
                    <div className="k-display-flex k-align-items-center">
                        {(valid === false || inactiveValue) && (
                            <div className={suffixIconGroupClasses}>
                                {valid === false && (
                                    <ValidationIconSuffix
                                        validationMessage={validationMessage}
                                        tooltipPosition={validationTooltipPosition}
                                    />
                                )}
                                {inactiveValue && (
                                    <InputSuffix>
                                        <InactiveIcon
                                            dataTestId={`${testId}-inactive`}
                                            isSuffix
                                            tooltipPosition="right"
                                        />
                                    </InputSuffix>
                                )}
                            </div>
                        )}
                        {!!value && !readOnly && showClear && (
                            <InputSuffix
                                data-testid={`${testId}-clear`}
                                onClick={() => {
                                    setInputValue('');
                                    if (onChange) {
                                        onChange({ target: { value: '' } } as TextAreaChangeEvent);
                                    }
                                }}
                            >
                                <SvgIcon icon={xIcon} size="large" className={svgStyles.svgClear} />
                            </InputSuffix>
                        )}
                        {copyBtn && (fillMode === undefined || fillMode === 'solid') && (
                            <InputSuffix
                                onClick={copyToClipboard}
                                className={cn(
                                    'k-d-flex k-justify-content-center k-w-8 k-h-full k-cursor-pointer opra-copy-btn',
                                    styles.copyBtn,
                                    { [styles.copyBtnError]: valid === false },
                                )}
                            >
                                <FontAwesomeIcon icon={['far', 'copy']} fontSize={18} className={styles.copyIcon} />
                            </InputSuffix>
                        )}
                    </div>
                )}
            />
            {maxLength && <CharactersValidationHint length={(inputValue as string)?.length || 0} max={maxLength} />}
        </div>
    );
};

interface TextAreaControllerProps<TData, TControl extends FieldValues> extends TextAreaProps {
    control: Control<TControl>;
    name: FieldPath<TControl>;
    validateRules?: Record<string, (value: TData | null) => string | boolean | undefined>;
    handleInputChange?: (event: TextAreaChangeEvent) => void;
}

const TextAreaController = <TData, TControl extends FieldValues>({
    control,
    name,
    disabled,
    'data-testid': dataTestId,
    required = false,
    label,
    maxLength,
    copyBtn,
    fillMode,
    readOnly,
    validateRules,
    handleInputChange,
    ...props
}: TextAreaControllerProps<TData, TControl>) => {
    const { t } = useTranslation();

    return (
        <Controller
            control={control}
            name={name}
            rules={{
                required: {
                    value: required,
                    message: t('ra:requiredFieldError'),
                },
                validate: {
                    maxLength: (value) => {
                        if (maxLength) {
                            return validateLimitExceeded(value as string, maxLength);
                        }
                        return true;
                    },
                    ...validateRules,
                },
            }}
            render={({ field: { onChange, value, name: fieldName }, fieldState: { error } }) => {
                return (
                    <TextArea
                        {...props}
                        name={fieldName}
                        data-testid={dataTestId}
                        label={label}
                        disabled={disabled}
                        valid={!error}
                        value={value as string}
                        validationMessage={error?.message}
                        onChange={(event) => {
                            onChange(event.target.value);
                            if (handleInputChange) handleInputChange(event);
                        }}
                        copyBtn={copyBtn}
                        fillMode={fillMode}
                        readOnly={readOnly}
                        maxLength={maxLength}
                    />
                );
            }}
        />
    );
};

export { TextArea, TextAreaController };
